import {Component, OnInit, ChangeDetectionStrategy, Output, EventEmitter, OnDestroy} from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../../shared/services/api.service';
import { Router } from '@angular/router';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import {Subject, throwError} from 'rxjs';
import {catchError, exhaustMap, switchMap, takeUntil, tap} from 'rxjs/operators';

interface Column {
  id: number;
  name: string;
  description: string;
  list: boolean;
  subColumns?: Column[];
  expanded?: boolean;
  readonly?: boolean;
}

interface PreviewColumn {
  name: string;
  value?: string;
  subColumns?: PreviewColumn[];
  expanded: boolean;
  readonly?: boolean;
}

interface Template {
  value: string;
  name: string;
}

type TabName = 'general' | 'columns' | 'manage';

@Component({
  selector: 'app-columns',
  templateUrl: './columns.component.html',
  styleUrls: ['./columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColumnsComponent implements OnInit, OnDestroy {
  @Output() tabChange = new EventEmitter<{tab: 'general' | 'columns' | 'manage', templateName: string}>();

  templateDescription = '';
  projectId: string;
  columnsForm: FormGroup;
  selectedTemplate = 'default';
  templateName = 'Default Template';  // New property for the template name
  previewColumns: PreviewColumn[] = [];
  private saveTrigger$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  templateOptions: Template[] = [
    { value: 'default', name: 'Default Template' },
    { value: 'actions', name: 'Actions Template' },
    { value: 'bdd', name: 'BDD Template (GIVEN-WHEN-THEN)' },
    { value: 'custom', name: 'Custom Template' }
  ];

  // Default standard columns that will be included in all templates
  standardColumns: Column[] = [
    { id: 1, name: 'ID', description: 'Unique identifier for the test case', list: false, readonly: true },
    { id: 2, name: 'TYPE', description: 'Test Typology', list: false, readonly: true }
  ];

  templates = {
    default: [
      { id: 3, name: 'TITLE', description: 'Brief title of the test case', list: false },
      { id: 4, name: 'OBJECTIVE', description: 'The goal or purpose of the test case', list: false },
      { id: 5, name: 'PRECONDITIONS', description: 'Any prerequisites or setup list for the test', list: true },
      { id: 6, name: 'STEPS', description: 'Detailed steps to execute the test', list: true },
      { id: 7, name: 'POSTCONDITION', description: 'The expected state after test execution', list: true },
    ],
    actions: [
      { id: 3, name: 'TITLE', description: 'Brief title of the test case', list: false },
      { id: 4, name: 'OBJECTIVE', description: 'The goal or purpose of the test case', list: false },
      { id: 5, name: 'PRECONDITIONS', description: 'Any prerequisites or setup list for the test', list: true },
      { id: 6, name: 'STEPS', description: 'Detailed steps to execute the test', list: true, subColumns: [
          { id: 61, name: 'ACTION', description: 'The action to be performed', list: false },
          { id: 62, name: 'DATA', description: 'Any data inputs list for the action', list: false },
          { id: 63, name: 'EXPECTED RESULT', description: 'The expected outcome of the action', list: false },
        ]},
      { id: 7, name: 'POSTCONDITION', description: 'The expected state after test execution', list: true },
    ],
    bdd: {
      description: `You are a Test Analyst writing test cases following the BDD structure but with a focus on clean, readable steps.

**CRITICAL RULE: DO NOT include Gherkin keywords.**

The text for each step inside the GIVEN, WHEN, and THEN columns **MUST NOT** begin with keywords like \`Given\`, \`When\`, \`Then\`, \`And\`, or \`But\`. The column header itself already provides this context. You must write only the factual statement.

**Correct vs. Incorrect Formatting Example:**

*   **INCORRECT:**
    \`\`\`html
    <ul>
      <li>Given l'utente è sulla pagina di login.</li>
      <li>And ha inserito credenziali valide.</li>
    </ul>
    \`\`\`
*   **CORRECT:**
    \`\`\`html
    <ul>
      <li>L'utente è sulla pagina di login.</li>
      <li>Ha inserito credenziali valide.</li>
    </ul>
    \`\`\`

*   **INCORRECT:**
    \`\`\`html
    <ul>
      <li>Then il sistema visualizza la dashboard.</li>
    </ul>
    \`\`\`
*   **CORRECT:**
    \`\`\`html
    <ul>
      <li>Il sistema visualizza la dashboard.</li>
    </ul>
    \`\`\`
`,
      columns: [
        { id: 3, name: 'TITLE', description: 'Brief title of the test case', list: false },
        { id: 4, name: 'GIVEN', description: 'Describes the preconditions. Each step MUST be a direct statement of fact, WITHOUT starting with "Given" or "And".', list: true },
        { id: 5, name: 'WHEN', description: 'Specifies the key action. The statement MUST NOT start with "When" or "And".', list: true },
        { id: 6, name: 'THEN', description: 'Describes the expected outcome. The statement MUST NOT start with "Then" or "And".', list: true },
      ]
    },
    custom: []
  };

  constructor(private router: Router, private fb: FormBuilder,
              private route: ActivatedRoute, private apiService: ApiService,
              private spinnerService: SpinnerService) { }

  ngOnInit(): void {
    this.initForm();
    this.loadTemplate();
    this.projectId = this.route.snapshot.params.project_id;
    this.setupSavePipeline(); // <-- Call the new method
  }

  private setupSavePipeline(): void {
    this.saveTrigger$.pipe(
      // exhaustMap is the key: it ignores new triggers while an existing operation is in progress.
      // This completely replaces the need for an `isSubmitting` flag.
      exhaustMap(() => {
        this.spinnerService.show(); // Show spinner at the very start of the operation.

        // This is our inner observable chain for the actual work.
        return this.apiService.getTemplates(this.projectId).pipe(
          switchMap(templates => {
            const templateExists = templates.some(template => template.templateName === this.templateName);
            if (templateExists) {
              // To stop the chain and trigger an error, we return a failing observable.
              // The error will be caught by our `catchError` operator below.
              return throwError(new Error('A template with this name already exists. Please choose a different name.'));
            }
            // If the check passes, we proceed to the save operation.
            return this.apiService.saveConfiguration(
              this.projectId,
              this.templateName,
              this.templateDescription,
              this.columnsForm.value.columns
            );
          }),
          // This `tap` operator will ONLY execute if the entire chain above succeeds.
          // It's the perfect place to put success-path cleanup.
          tap(() => {
            this.spinnerService.hide();
          }),
          // This `catchError` will ONLY execute if any part of the chain above fails.
          catchError(err => {
            // Perform error-path cleanup.
            this.spinnerService.hide();
            // IMPORTANT: Re-throw the error so the final `subscribe` block's error handler receives it.
            return throwError(err);
          })
        );
      }),
      // This ensures the entire pipeline is torn down if the component is destroyed.
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // This block now only contains logic for a successful outcome.
        alert('Configuration saved successfully!');
        console.log('Configuration saved successfully:', response);
        this.tabChange.emit({ tab: 'manage', templateName: this.templateName });
      },
      error: (err) => {
        // This block now only contains logic for a failed outcome.
        // The error can be from an API failure or our business logic check.
        alert(err.message || 'An error occurred while saving.');
        console.error('Error during save process:', err);
      }
    });
  }

  initForm(): void {
    this.columnsForm = this.fb.group({
      columns: this.fb.array([])
    });
  }

  loadTemplate(): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    columnsArray.clear();

    // First add the standard columns that all templates must have
    this.standardColumns.forEach(column => {
      columnsArray.push(this.createColumnGroup(column));
    });

    if (this.selectedTemplate === 'bdd') {
      this.templates.bdd.columns.forEach(column => {
        columnsArray.push(this.createColumnGroup(column));
      });
      this.templateDescription = this.templates.bdd.description;
    } else {
      this.templates[this.selectedTemplate].forEach(column => {
        columnsArray.push(this.createColumnGroup(column));
      });
      this.templateDescription = ''; // Clear description for other templates
    }

    this.templateName = this.templateOptions.find(template => template.value === this.selectedTemplate)?.name || 'Custom Template';
    this.updatePreview();
  }

  createColumnGroup(column: Column): FormGroup {
    const group = this.fb.group({
      id: [column.id],
      name: [column.name],
      description: [column.description],
      list: [column.list],
      expanded: [column.expanded || false],
      readonly: [column.readonly || false],
      subColumns: this.fb.array([])
    });

    if (column.subColumns) {
      column.subColumns.forEach(subColumn => {
        (group.get('subColumns') as FormArray).push(this.createColumnGroup(subColumn));
      });
    }

    return group;
  }

  handleTemplateChange(): void {
    this.loadTemplate();
  }

  addColumn(): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    const newId = Math.max(...columnsArray.value.map((c: Column) => c.id), 0) + 1;
    columnsArray.push(this.createColumnGroup({ id: newId, name: '', description: '', list: false }));
    this.updatePreview();
  }

  removeColumn(index: number): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    const column = columnsArray.at(index);

    // Prevent removing standard columns
    if (column.get('readonly').value) {
      alert('Standard columns cannot be removed.');
      return;
    }

    columnsArray.removeAt(index);
    this.updatePreview();
  }

  addSubColumn(columnIndex: number): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    const column = columnsArray.at(columnIndex);

    // Prevent adding subcolumns to standard columns
    if (column.get('readonly').value) {
      alert('Standard columns cannot be modified.');
      return;
    }

    const subColumnsArray = column.get('subColumns') as FormArray;
    const newId = Math.max(...subColumnsArray.value.map((sf: Column) => sf.id), columnsArray.at(columnIndex).value.id * 10) + 1;
    subColumnsArray.push(this.createColumnGroup({ id: newId, name: '', description: '', list: false }));
    columnsArray.at(columnIndex).patchValue({ expanded: true });
    this.updatePreview();
  }

  removeSubColumn(columnIndex: number, subColumnIndex: number): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    const subColumnsArray = columnsArray.at(columnIndex).get('subColumns') as FormArray;
    subColumnsArray.removeAt(subColumnIndex);
    this.updatePreview();
  }

  toggleExpand(columnForm: FormGroup): void {
    columnForm.patchValue({ expanded: !columnForm.get('expanded').value });
  }

  updatePreview(): void {
    const columnsArray = this.columnsForm.get('columns') as FormArray;
    this.previewColumns = columnsArray.value.map((column: Column) => {
      const previewColumn: PreviewColumn = {
        name: column.name,
        expanded: false,
        readonly: column.readonly
      };

      if (column.subColumns && column.subColumns.length > 0) {
        previewColumn.subColumns = column.subColumns.map(subColumn => ({
          name: subColumn.name,
          value: 'Test',
          expanded: false
        }));
      } else {
        previewColumn.value = 'Test';
      }

      return previewColumn;
    });
  }

  togglePreviewExpand(column: PreviewColumn): void {
    column.expanded = !column.expanded;
  }

  previewConfiguration(): void {
    this.updatePreview();
  }

  saveConfiguration(): void {
    // Just trigger the pipeline. exhaustMap handles the rest.
    this.saveTrigger$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

